import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { AtlasRecord, CreateRecordInput, UsStateCode } from '@grs-atlas/shared';
import { isEditor } from '@grs-atlas/shared';
import { UsMap } from '../maps/UsMap';
import { FilterBar } from '../filters/FilterBar';
import { RecordsTable } from '../records/RecordsTable';
import { DetailPanel } from '../records/DetailPanel';
import { RecordForm } from '../records/RecordForm';
import {
  useArchiveRecord,
  useCreateRecord,
  useExport,
  useMap,
  useRecordHistory,
  useRecords,
  useRestoreVersion,
  useStates,
  useUpdateRecord,
} from '../../services/hooks';
import { ApiClientError } from '../../services/api';
import { useAuthRoles } from '../../auth/AuthContext';
import { Toast } from '../../components/Toast';

type ViewMode = 'map' | 'split' | 'table';

const SPLIT_RATIO_KEY = 'atlas-map-split-ratio';
const DEFAULT_SPLIT_RATIO = 55;
const MIN_SPLIT_RATIO = 25;
const MAX_SPLIT_RATIO = 75;
/** UI-only URL params — must not be sent to records/states APIs as field filters. */
const UI_SEARCH_PARAMS = new Set(['view']);

function clampSplitRatio(value: number): number {
  return Math.min(MAX_SPLIT_RATIO, Math.max(MIN_SPLIT_RATIO, value));
}

function readStoredSplitRatio(): number {
  try {
    const raw = localStorage.getItem(SPLIT_RATIO_KEY);
    if (raw == null) return DEFAULT_SPLIT_RATIO;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? clampSplitRatio(parsed) : DEFAULT_SPLIT_RATIO;
  } catch {
    return DEFAULT_SPLIT_RATIO;
  }
}

export function MapPage() {
  const { mapId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const roles = useAuthRoles();
  const canEdit = isEditor(roles);
  const layoutRef = useRef<HTMLDivElement>(null);
  const [splitRatio, setSplitRatio] = useState(readStoredSplitRatio);
  const [isResizing, setIsResizing] = useState(false);

  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    searchParams.forEach((v, k) => {
      if (!UI_SEARCH_PARAMS.has(k)) f[k] = v;
    });
    return f;
  }, [searchParams]);

  const selectedState = filters.state;
  const view = (searchParams.get('view') as ViewMode) || 'split';

  const setFilters = (next: Record<string, string>) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(next)) {
      if (v && !UI_SEARCH_PARAMS.has(k)) params.set(k, v);
    }
    if (view !== 'split') params.set('view', view);
    setSearchParams(params);
  };

  const setView = (v: ViewMode) => {
    const params = new URLSearchParams(searchParams);
    if (v === 'split') params.delete('view');
    else params.set('view', v);
    setSearchParams(params);
  };

  const selectState = (code: string | undefined) => {
    const next = { ...filters };
    if (!code) delete next.state;
    else next.state = code;
    setFilters(next);
  };

  // Omit selected state so map tooltips / coloring stay global; table filters client-side.
  const recordFilters = useMemo(() => {
    const { state: _state, ...rest } = filters;
    return rest;
  }, [filters]);

  const mapQuery = useMap(mapId);
  const recordsQuery = useRecords(mapId, recordFilters);
  const statesQuery = useStates(mapId, filters);

  const createMutation = useCreateRecord(mapId);
  const updateMutation = useUpdateRecord(mapId);
  const archiveMutation = useArchiveRecord(mapId);
  const exportMutation = useExport(mapId);
  const restoreMutation = useRestoreVersion(mapId);

  const [modal, setModal] = useState<'create' | 'edit' | 'history' | null>(null);
  const [activeRecord, setActiveRecord] = useState<AtlasRecord | null>(null);
  const [formError, setFormError] = useState<string>();
  const [toast, setToast] = useState<{ message: string; type?: 'error' | 'success' } | null>(
    null,
  );
  const [conflict, setConflict] = useState<{
    message: string;
    currentVersion?: number;
  } | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(SPLIT_RATIO_KEY, String(splitRatio));
    } catch {
      // ignore quota / private mode failures
    }
  }, [splitRatio]);

  useEffect(() => {
    if (!isResizing) return;
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const onPointerMove = (event: PointerEvent) => {
      const layout = layoutRef.current;
      if (!layout) return;
      const { left, width } = layout.getBoundingClientRect();
      if (width <= 0) return;
      setSplitRatio(clampSplitRatio(((event.clientX - left) / width) * 100));
    };

    const stopResizing = () => setIsResizing(false);

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stopResizing);
    window.addEventListener('pointercancel', stopResizing);
    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stopResizing);
      window.removeEventListener('pointercancel', stopResizing);
    };
  }, [isResizing]);

  const historyQuery = useRecordHistory(
    mapId,
    modal === 'history' ? activeRecord?.recordId : undefined,
  );

  if (mapQuery.isLoading) return <div className="empty">Loading map…</div>;
  if (mapQuery.isError || !mapQuery.data) {
    return (
      <div className="empty">
        Map not found. <Link to="/maps">Back to maps</Link>
      </div>
    );
  }

  const map = mapQuery.data;
  const allRecords = recordsQuery.data?.items ?? [];
  const records = selectedState
    ? allRecords.filter((r) => r.stateCodes.includes(selectedState as UsStateCode))
    : allRecords;
  const states = statesQuery.data?.states ?? [];
  const stateSummary = states.find((s) => s.stateCode === selectedState);

  const layoutClass =
    view === 'table'
      ? 'map-layout table-only'
      : view === 'map'
        ? 'map-layout'
        : `map-layout split${isResizing ? ' resizing' : ''}`;

  const splitLayoutStyle =
    view === 'split'
      ? ({
          ['--split-ratio' as string]: `${splitRatio}%`,
        } as const)
      : undefined;

  const handleCreate = async (data: CreateRecordInput) => {
    setFormError(undefined);
    try {
      await createMutation.mutateAsync(data);
      setModal(null);
      setToast({ message: 'Record created.', type: 'success' });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create');
    }
  };

  const handleUpdate = async (data: CreateRecordInput) => {
    if (!activeRecord) return;
    setFormError(undefined);
    setConflict(null);
    try {
      await updateMutation.mutateAsync({
        recordId: activeRecord.recordId,
        input: {
          expectedVersion: activeRecord.version,
          changes: {
            name: data.name,
            stateCodes: data.stateCodes,
            fields: data.fields,
            tags: data.tags,
          },
        },
      });
      setModal(null);
      setActiveRecord(null);
      setToast({ message: 'Record updated.', type: 'success' });
    } catch (err) {
      if (err instanceof ApiClientError && err.body.error === 'VERSION_CONFLICT') {
        setConflict({
          message: err.body.message,
          currentVersion: err.body.currentVersion,
        });
      } else {
        setFormError(err instanceof Error ? err.message : 'Failed to update');
      }
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: '1.25rem' }}>{map.name}</h1>
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.9rem' }}>{map.description}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', maxWidth: '100%' }}>
          <div className="view-toggle" role="group" aria-label="View mode">
            {(['map', 'split', 'table'] as ViewMode[]).map((v) => (
              <button
                key={v}
                type="button"
                className={view === v ? 'active' : ''}
                onClick={() => setView(v)}
              >
                {v[0]!.toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn secondary"
            onClick={async () => {
              try {
                const result = await exportMutation.mutateAsync({
                  filters: Object.fromEntries(
                    Object.entries(filters).filter(([, v]) => v),
                  ) as Record<string, string>,
                });
                window.open(result.downloadUrl, '_blank');
              } catch (err) {
                setToast({
                  message: err instanceof Error ? err.message : 'Export failed',
                  type: 'error',
                });
              }
            }}
          >
            Export CSV
          </button>
          {canEdit && (
            <Link className="btn secondary" to={`/maps/${mapId}/import`}>
              Import
            </Link>
          )}
        </div>
      </div>

      <FilterBar map={map} values={filters} onChange={setFilters} />

      <div ref={layoutRef} className={layoutClass} style={splitLayoutStyle}>
        {view !== 'table' && (
          <div className="map-pane">
            <UsMap
              states={states}
              map={map}
              records={allRecords}
              selectedState={selectedState}
              onSelectState={selectState}
            />
          </div>
        )}
        {view === 'split' && (
          <div
            className="split-handle"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize map and data panels"
            aria-valuemin={MIN_SPLIT_RATIO}
            aria-valuemax={MAX_SPLIT_RATIO}
            aria-valuenow={Math.round(splitRatio)}
            tabIndex={0}
            onPointerDown={(event) => {
              event.preventDefault();
              setIsResizing(true);
            }}
            onDoubleClick={() => setSplitRatio(DEFAULT_SPLIT_RATIO)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft') {
                event.preventDefault();
                setSplitRatio((current) => clampSplitRatio(current - 2));
              } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                setSplitRatio((current) => clampSplitRatio(current + 2));
              } else if (event.key === 'Home') {
                event.preventDefault();
                setSplitRatio(MIN_SPLIT_RATIO);
              } else if (event.key === 'End') {
                event.preventDefault();
                setSplitRatio(MAX_SPLIT_RATIO);
              }
            }}
          />
        )}
        {view !== 'map' && (
          <div className="detail-pane">
            {view === 'table' ? (
              <RecordsTable
                map={map}
                records={records}
                canEdit={canEdit}
                onEdit={(r) => {
                  setActiveRecord(r);
                  setModal('edit');
                }}
                onSelect={(r) => {
                  setActiveRecord(r);
                  setModal('edit');
                }}
              />
            ) : (
              <DetailPanel
                map={map}
                stateCode={selectedState}
                stateMetrics={
                  stateSummary
                    ? {
                        recordCount: stateSummary.recordCount,
                        label: String(stateSummary.metrics.label ?? ''),
                      }
                    : undefined
                }
                records={records}
                canEdit={canEdit}
                onAdd={() => {
                  setActiveRecord(null);
                  setModal('create');
                }}
                onEdit={(r) => {
                  setActiveRecord(r);
                  setModal('edit');
                }}
                onViewHistory={(r) => {
                  setActiveRecord(r);
                  setModal('history');
                }}
                onArchive={async (r) => {
                  try {
                    await archiveMutation.mutateAsync(r.recordId);
                    setToast({ message: 'Record archived.', type: 'success' });
                  } catch (err) {
                    setToast({
                      message: err instanceof Error ? err.message : 'Archive failed',
                      type: 'error',
                    });
                  }
                }}
              />
            )}
          </div>
        )}
      </div>

      {(modal === 'create' || modal === 'edit') && (
        <div className="modal-backdrop" role="presentation" onClick={() => setModal(null)}>
          <div
            className="modal"
            role="dialog"
            aria-modal
            aria-labelledby="record-form-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="record-form-title">
              {modal === 'create' ? `Add ${map.recordLabel}` : `Edit ${map.recordLabel}`}
            </h2>
            {conflict && (
              <div role="alert" style={{ marginBottom: 12, color: 'var(--warning)' }}>
                {conflict.message} (current version {conflict.currentVersion}). Reload the record
                and reapply your changes.
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn secondary"
                    onClick={() => {
                      void recordsQuery.refetch().then((res) => {
                        const fresh = res.data?.items.find(
                          (r) => r.recordId === activeRecord?.recordId,
                        );
                        if (fresh) setActiveRecord(fresh);
                        setConflict(null);
                      });
                    }}
                  >
                    Reload record
                  </button>
                </div>
              </div>
            )}
            <RecordForm
              map={map}
              initial={modal === 'edit' ? activeRecord ?? undefined : undefined}
              defaultState={selectedState}
              onSubmit={modal === 'create' ? handleCreate : handleUpdate}
              onCancel={() => setModal(null)}
              submitting={createMutation.isPending || updateMutation.isPending}
              error={formError}
            />
          </div>
        </div>
      )}

      {modal === 'history' && activeRecord && (
        <div className="modal-backdrop" role="presentation" onClick={() => setModal(null)}>
          <div
            className="modal"
            role="dialog"
            aria-modal
            aria-labelledby="history-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="history-title">History — {activeRecord.name}</h2>
            {historyQuery.isLoading && <p>Loading…</p>}
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {(historyQuery.data?.items ?? []).map((h) => (
                <li
                  key={h.version}
                  className="panel"
                  style={{ padding: '0.75rem', marginBottom: 8 }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong>
                      v{h.version} · {h.operation}
                    </strong>
                    <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                      {new Date(h.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                    {h.userEmail}
                    {h.importId ? ` · import ${h.importId}` : ''}
                  </div>
                  {roles.includes('Atlas.Admin') && (
                    <button
                      type="button"
                      className="btn secondary"
                      style={{ marginTop: 8 }}
                      onClick={async () => {
                        try {
                          await restoreMutation.mutateAsync({
                            recordId: activeRecord.recordId,
                            version: h.version,
                          });
                          setToast({ message: `Restored version ${h.version}.`, type: 'success' });
                          setModal(null);
                        } catch (err) {
                          setToast({
                            message: err instanceof Error ? err.message : 'Restore failed',
                            type: 'error',
                          });
                        }
                      }}
                    >
                      Restore this version
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <div className="form-actions">
              <button type="button" className="btn secondary" onClick={() => setModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
